import axios, { AxiosInstance, AxiosError } from 'axios';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { Customer, Appointment, Service, AgentConfig, ApiResponse, Vendor, UserPreferences, Conversation, Message, MenuItem, Reward, CustomerPoints, Order } from '../types';

// Ensure API URL is properly formatted
const getApiBaseUrl = (): string => {
  // Use environment variable, or fallback to API Gateway URL if custom domain DNS not resolved
  // Custom domain: api.reachoutaiapp.com (DNS propagating)
  // Fallback: Direct API Gateway URL (works immediately)
  let envUrl = process.env.REACT_APP_API_ENDPOINT || 
    'https://hep8t7i0l7.execute-api.us-east-1.amazonaws.com/prod';
  
  // For custom domains (api.reachoutaiapp.com), remove /v1 if present
  // For API Gateway URLs (execute-api), keep /prod stage name
  if (envUrl.includes('api.reachoutaiapp.com')) {
    // Custom domain - remove /v1 if present
    envUrl = envUrl.replace(/\/v1\/?$/, '');
  } else if (envUrl.includes('execute-api')) {
    // API Gateway URL - ensure /prod is present and remove trailing slashes
    envUrl = envUrl.replace(/\/+$/, ''); // Remove trailing slashes
    if (!envUrl.endsWith('/prod')) {
      envUrl = envUrl + '/prod';
    }
  }
  
  return envUrl;
};

const API_BASE_URL = getApiBaseUrl();

class ApiService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add auth token and impersonation header to requests
    this.client.interceptors.request.use(async (config) => {
      try {
        const skipSelectedVendor = (config as any)?.skipSelectedVendor === true;
        const url = config.url || '';
        const isPublic = url.startsWith('/demo') || url.startsWith('/webhooks') || url.includes('/queue-verify') || url.includes('/payment-details');
        
        // For public endpoints, skip auth
        if (isPublic) {
          return config;
        }
        
        // For protected endpoints, get auth token
        const session = await fetchAuthSession();
        // For API Gateway REST APIs using a Cognito User Pool authorizer, use the **ID token**.
        // (Access tokens frequently cause 401s with this authorizer configuration.)
        // API Gateway Cognito authorizers require the "Bearer " prefix.
        const idToken = session.tokens?.idToken;
        let token: string | undefined;
        
        // Handle different token formats - could be a string or an object with toString()
        if (typeof idToken === 'string') {
          token = idToken;
        } else if (idToken) {
          // Try toString() method
          token = idToken.toString();
          // If toString() doesn't work, try to get the token string directly
          if (!token || token === '[object Object]') {
            // Try accessing token property if it's an object
            token = (idToken as any)?.token || (idToken as any)?.toString?.() || undefined;
          }
        }
        
        if (token) {
          // Ensure we have a valid JWT token (should start with eyJ)
          if (token.startsWith('eyJ')) {
            config.headers.Authorization = `Bearer ${token}`;
          } else {
            console.warn('⚠️ Invalid token format - token does not start with eyJ:', token.substring(0, 20));
            // Still try to use it, but log a warning
            config.headers.Authorization = `Bearer ${token}`;
          }
        } else {
          // If no token, abort protected API requests to avoid 401 + noisy CORS errors.
          console.warn('⚠️ No auth token available for protected route:', url);
          const err: any = new Error('Not authenticated');
          err.code = 'NO_AUTH_TOKEN';
          return Promise.reject(err);
        }
        
        // Add impersonation header if set (for admin use)
        const impersonateVendorId = localStorage.getItem('impersonateVendorId');
        if (impersonateVendorId) {
          config.headers['X-Impersonate-Vendor-Id'] = impersonateVendorId;
        }
        
        // Add selected vendor header if set (for users with multiple businesses)
        // Some calls (like listing all businesses for a switcher) must skip this header.
        if (!skipSelectedVendor) {
          // Try to load from sessionStorage cache first (set by ProtectedRoute)
          // Check multiple sources to ensure we find it
          let selectedVendorId = 
            sessionStorage.getItem('selectedVendorId') || 
            localStorage.getItem('selectedVendorId');
          
          // If not in cache, try to load from cached UserPreferences
          if (!selectedVendorId) {
            try {
              const cachedPrefs = sessionStorage.getItem('userPreferences');
              if (cachedPrefs) {
                const prefs = JSON.parse(cachedPrefs);
                selectedVendorId = prefs.selectedVendorId;
                if (selectedVendorId) {
                  // Cache it for next time
                  sessionStorage.setItem('selectedVendorId', selectedVendorId);
                  localStorage.setItem('selectedVendorId', selectedVendorId);
                }
              }
            } catch (e) {
              // Ignore errors, fallback to localStorage
            }
          }
          
          if (selectedVendorId) {
            config.headers['X-Selected-Vendor-Id'] = selectedVendorId;
          } else {
            // Log warning if we're making a protected API call without vendorId
            // This helps debug why 401s are happening
            const url = config.url || '';
            const isProtected = !url.startsWith('/demo') && !url.startsWith('/webhooks');
            if (isProtected && token) {
              console.warn('⚠️ No selectedVendorId found for protected route:', url, '- request may fail with 401');
            }
          }
        }
        // Note: Don't warn if selectedVendorId is missing - it will be set after first API call succeeds
      } catch (error: any) {
        // Handle auth session errors gracefully
        if (error?.name === 'NotAuthorizedException' || error?.message?.includes('cognito-identity')) {
          console.warn('⚠️ Auth session error (may be transient during OAuth redirect):', error.message);
          // Allow the request to proceed without auth for public endpoints
          const url = config.url || '';
          const isPublic = url.startsWith('/demo') || url.startsWith('/webhooks') || url.includes('/queue-verify') || url.includes('/payment-details');
          if (!isPublic) {
            // For protected endpoints, reject the request
            const err: any = new Error('Authentication session unavailable');
            err.code = 'AUTH_SESSION_ERROR';
            return Promise.reject(err);
          }
        }
        // Don't log other auth errors - they're expected when not logged in
      }
      return config;
    });
    
    // Add response interceptor to handle 401/403 errors with better debugging and token refresh
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as any;
        
        // Log 401/403 errors with details for debugging
        if (error.response?.status === 403 || error.response?.status === 401) {
          const hasToken = originalRequest?.headers?.Authorization;
          const url = originalRequest?.url || 'unknown';
          const authHeader = originalRequest?.headers?.Authorization;
          
          // Log detailed error info for debugging
          console.error('🔴 Auth Error:', {
            status: error.response?.status,
            url,
            hasToken: !!hasToken,
            authHeaderPrefix: authHeader ? authHeader.substring(0, 30) + '...' : 'none',
            responseBody: error.response?.data,
            responseHeaders: error.response?.headers,
          });
          
          // If we have a token but still got 401, try refreshing the session
          if (hasToken && error.response?.status === 401 && !originalRequest._retry) {
            console.warn('⚠️ Token present but request was rejected - attempting token refresh...');
            originalRequest._retry = true;
            
            try {
              // Force a fresh session fetch (this may trigger token refresh)
              const freshSession = await fetchAuthSession({ forceRefresh: true });
              const freshToken = freshSession.tokens?.idToken?.toString();
              
              if (freshToken) {
                console.log('✅ Got fresh token, retrying request...');
                originalRequest.headers.Authorization = `Bearer ${freshToken}`;
                return this.client(originalRequest);
              } else {
                console.error('❌ No token available after refresh - signing out');
                await this.handleAuthFailure();
              }
            } catch (refreshError) {
              console.error('❌ Failed to refresh token - signing out:', refreshError);
              await this.handleAuthFailure();
            }
          } else if (hasToken && (error.response?.status === 401 || error.response?.status === 403) && originalRequest._retry) {
            // Token refresh was attempted but still failed - sign out
            console.error('❌ Persistent auth error after refresh - signing out');
            await this.handleAuthFailure();
          }
        }
        return Promise.reject(error);
      }
    );
  }

  // Handle persistent authentication failures
  private async handleAuthFailure(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Error during sign out:', error);
    }
    
    // Clear local storage
    sessionStorage.clear();
    localStorage.removeItem('selectedVendorId');
    
    // Redirect to login
    window.location.href = '/login';
  }

  // Expose axios instance for direct use
  get(url: string, config?: any) {
    return this.client.get(url, config);
  }

  post(url: string, data?: any, config?: any) {
    return this.client.post(url, data, config);
  }

  put(url: string, data?: any, config?: any) {
    return this.client.put(url, data, config);
  }

  delete(url: string, config?: any) {
    return this.client.delete(url, config);
  }

  // Customers
  async getCustomers(params?: { status?: string; search?: string }): Promise<Customer[]> {
    const response = await this.client.get<ApiResponse<{ customers: Customer[] }>>('/customers', { params });
    return response.data.data?.customers || [];
  }

  async getCustomer(customerId: string): Promise<Customer> {
    const response = await this.client.get<ApiResponse<Customer>>(`/customers/${customerId}`);
    return response.data.data!;
  }

  async createCustomer(customer: Partial<Customer>): Promise<Customer> {
    const response = await this.client.post<ApiResponse<Customer>>('/customers', customer);
    return response.data.data!;
  }

  async updateCustomer(customerId: string, updates: Partial<Customer>): Promise<Customer> {
    const response = await this.client.put<ApiResponse<Customer>>(`/customers/${customerId}`, updates);
    return response.data.data!;
  }

  async deleteCustomer(customerId: string): Promise<void> {
    await this.client.delete(`/customers/${customerId}`);
  }

  // Appointments
  async getAppointments(params?: { status?: string; customerId?: string; date?: string }): Promise<Appointment[]> {
    const response = await this.client.get<ApiResponse<{ appointments: Appointment[] }>>('/appointments', { params });
    return response.data.data?.appointments || [];
  }

  async getAppointment(appointmentId: string): Promise<Appointment> {
    const response = await this.client.get<ApiResponse<Appointment>>(`/appointments/${appointmentId}`);
    return response.data.data!;
  }

  async createAppointment(appointment: Partial<Appointment>): Promise<Appointment> {
    const response = await this.client.post<ApiResponse<Appointment>>('/appointments', appointment);
    return response.data.data!;
  }

  async updateAppointment(appointmentId: string, updates: Partial<Appointment>): Promise<Appointment> {
    const response = await this.client.put<ApiResponse<Appointment>>(`/appointments/${appointmentId}`, updates);
    return response.data.data!;
  }

  async cancelAppointment(appointmentId: string): Promise<void> {
    await this.client.delete(`/appointments/${appointmentId}`);
  }

  // Orders
  async getOrders(params?: { status?: string; paymentStatus?: string; customerId?: string; date?: string }): Promise<Order[]> {
    const response = await this.client.get<ApiResponse<{ orders: Order[] }>>('/orders', { params });
    return response.data.data?.orders || [];
  }

  async getOrder(orderId: string): Promise<Order> {
    const response = await this.client.get<ApiResponse<Order>>(`/orders/${orderId}`);
    return response.data.data!;
  }

  async updateOrder(orderId: string, updates: Partial<Order>): Promise<Order> {
    const response = await this.client.patch<ApiResponse<Order>>(`/orders/${orderId}`, updates);
    return response.data.data!;
  }

  // Employee Queue Access APIs
  async verifyQueueAccess(vendorId: string, pin: string): Promise<{ verified: boolean; vendor?: Vendor }> {
    try {
      const response = await this.client.post(
        `/vendors/${vendorId}/queue-verify`,
        { pin },
        { skipSelectedVendor: true } as any // Public endpoint, no vendor auth needed
      );
      
      // Backend returns data directly (not wrapped in ApiResponse for public endpoints)
      // Check if it's wrapped or direct
      if (response.data && typeof response.data === 'object') {
        if ('data' in response.data && response.data.data) {
          // Wrapped format: { data: { verified, vendor } }
          return response.data.data;
        } else if ('verified' in response.data) {
          // Direct format: { verified, vendor }
          return response.data;
        }
      }
      
      // Fallback: try to extract from response
      return response.data as { verified: boolean; vendor?: Vendor };
    } catch (error: any) {
      console.error('Error in verifyQueueAccess:', error);
      // If it's a 401 or 403, the PIN is invalid
      if (error.response?.status === 401 || error.response?.status === 403) {
        return { verified: false };
      }
      throw error;
    }
  }

  async getOrdersForEmployee(vendorId: string, pin: string, params?: { status?: string; date?: string }): Promise<Order[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.date) queryParams.append('date', params.date);
    queryParams.append('pin', pin);
    
    const response = await this.client.get<ApiResponse<Order[]>>(
      `/orders?${queryParams.toString()}`,
      { skipSelectedVendor: true } as any
    );
    return response.data.data || [];
  }

  async getAppointmentsForEmployee(vendorId: string, pin: string, params?: { status?: string; date?: string }): Promise<Appointment[]> {
    const queryParams = new URLSearchParams();
    if (params?.status) queryParams.append('status', params.status);
    if (params?.date) queryParams.append('date', params.date);
    queryParams.append('pin', pin);
    
    const response = await this.client.get<ApiResponse<Appointment[]>>(
      `/appointments?${queryParams.toString()}`,
      { skipSelectedVendor: true } as any
    );
    return response.data.data || [];
  }

  async updateOrderForEmployee(vendorId: string, orderId: string, pin: string, updates: Partial<Order>): Promise<Order> {
    const response = await this.client.patch<ApiResponse<Order>>(
      `/orders/${orderId}?pin=${pin}`,
      updates,
      { skipSelectedVendor: true } as any
    );
    return response.data.data!;
  }

  async updateAppointmentForEmployee(vendorId: string, appointmentId: string, pin: string, updates: Partial<Appointment>): Promise<Appointment> {
    const response = await this.client.patch<ApiResponse<Appointment>>(
      `/appointments/${appointmentId}?pin=${pin}`,
      updates,
      { skipSelectedVendor: true } as any
    );
    return response.data.data!;
  }

  // Menu Management
  async getMenuItems(): Promise<MenuItem[]> {
    const response = await this.client.get<ApiResponse<MenuItem[]>>('/menu');
    return response.data.data || [];
  }

  async createMenuItem(item: Partial<MenuItem>): Promise<MenuItem> {
    const response = await this.client.post<ApiResponse<MenuItem>>('/menu', item);
    return response.data.data!;
  }

  async getMenuItem(itemId: string): Promise<MenuItem> {
    const response = await this.client.get<ApiResponse<MenuItem>>(`/menu/${itemId}`);
    return response.data.data!;
  }

  async updateMenuItem(itemId: string, updates: Partial<MenuItem>): Promise<MenuItem> {
    const response = await this.client.put<ApiResponse<MenuItem>>(`/menu/${itemId}`, updates);
    return response.data.data!;
  }

  async deleteMenuItem(itemId: string): Promise<void> {
    await this.client.delete(`/menu/${itemId}`);
  }

  async getTaxConfig(): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>('/menu/tax-config');
    return response.data.data!;
  }

  async updateTaxConfig(config: any): Promise<any> {
    const response = await this.client.put<ApiResponse<any>>('/menu/tax-config', config);
    return response.data.data!;
  }

  async importMenuFromSquare(): Promise<{ imported: number; updated: number; total: number; items: MenuItem[]; lastSync: string; message: string }> {
    const response = await this.client.post<ApiResponse<{ imported: number; updated: number; total: number; items: MenuItem[]; lastSync: string; message: string }>>('/menu/import/square');
    return response.data.data!;
  }

  // Messages
  async getConversations(params?: { status?: string; customerId?: string; channel?: string }): Promise<Conversation[]> {
    const response = await this.client.get<ApiResponse<{ conversations: Conversation[] }>>('/messages', { params });
    return response.data.data?.conversations || [];
  }

  async getConversationMessages(conversationId: string): Promise<{ conversation: Conversation; messages: Message[] }> {
    const response = await this.client.get<ApiResponse<{ conversation: Conversation; messages: Message[] }>>(`/messages/conversations/${conversationId}`);
    return response.data.data!;
  }

  async sendMessage(data: { customerId: string; content: string; channel?: string; sender?: string }): Promise<any> {
    const response = await this.client.post<ApiResponse>('/messages/send', data);
    return response.data.data;
  }

  // AI Agent
  async chatWithAgent(data: { customerId: string; message: string; conversationId?: string }): Promise<any> {
    const response = await this.client.post<ApiResponse>('/ai/chat', data);
    return response.data.data;
  }

  // Services (for appointment booking)
  async getServices(): Promise<Service[]> {
    const response = await this.client.get<ApiResponse<Service[]>>('/services');
    return response.data.data || [];
  }

  async createService(service: Partial<Service>): Promise<Service> {
    const response = await this.client.post<ApiResponse<Service>>('/services', service);
    return response.data.data!;
  }

  // Agent Configuration
  async getAgentConfig(): Promise<AgentConfig> {
    const response = await this.client.get<ApiResponse<AgentConfig>>('/agent/config');
    return response.data.data!;
  }

  async saveAgentConfig(config: Partial<AgentConfig>): Promise<AgentConfig> {
    const response = await this.client.put<ApiResponse<AgentConfig>>('/agent/config', config);
    return response.data.data!;
  }

  // Payment Configuration
  async getPaymentConfig(): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>('/payments/config');
    return response.data.data!;
  }

  async savePaymentConfig(config: any): Promise<any> {
    const response = await this.client.put<ApiResponse<any>>('/payments/config', config);
    return response.data.data!;
  }

  // Integration APIs
  async getIntegration(provider: 'square' | 'stripe'): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>(`/integrations/${provider}`);
    return response.data.data!;
  }

  async saveIntegration(provider: 'square' | 'stripe', config: {
    accessToken?: string;
    locationId?: string;
    publishableKey?: string;
    secretKey?: string;
  }): Promise<any> {
    const response = await this.client.put<ApiResponse<any>>(`/integrations/${provider}`, config);
    return response.data.data!;
  }

  async deleteIntegration(provider: 'square' | 'stripe'): Promise<void> {
    await this.client.delete(`/integrations/${provider}`);
  }

  // Square OAuth
  async initiateSquareOAuth(): Promise<{ authUrl: string; state: string }> {
    const response = await this.client.get<ApiResponse<{ authUrl: string; state: string }>>('/oauth/square/authorize');
    
    // Handle different response structures
    if (response.data.data) {
      return response.data.data;
    } else if (response.data && typeof response.data === 'object' && 'authUrl' in response.data && 'state' in response.data) {
      return { authUrl: (response.data as any).authUrl, state: (response.data as any).state };
    }
    
    throw new Error('Invalid response structure from Square OAuth endpoint');
  }

  async getPayoutConfig(): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>('/integrations/payout');
    return response.data.data!;
  }

  async savePayoutConfig(config: {
    method: 'stripe' | 'square' | 'bank';
    bankAccountName?: string;
    bankAccountNumber?: string;
    bankRoutingNumber?: string;
  }): Promise<any> {
    const response = await this.client.put<ApiResponse<any>>('/integrations/payout', config);
    return response.data.data!;
  }

  // User Preferences APIs
  async getUserPreferences(): Promise<UserPreferences> {
    const response = await this.client.get<ApiResponse<UserPreferences>>('/user/preferences');
    return response.data.data!;
  }

  async updateUserPreferences(preferences: Partial<UserPreferences>): Promise<UserPreferences> {
    const response = await this.client.put<ApiResponse<UserPreferences>>('/user/preferences', preferences);
    return response.data.data!;
  }

  // Admin APIs
  async getAdminStats(): Promise<any> {
    const response = await this.client.get('/admin/stats');
    return response.data;
  }

  async getVendors(): Promise<any[]> {
    const response = await this.client.get('/vendors');
    // Backend returns raw data (not wrapped in { data })
    const data = response.data;
    return Array.isArray(data) ? data : data ? [data] : [];
  }

  // List all businesses the current user can access (ignores the selected vendor header)
  async listMyVendors(): Promise<Vendor[]> {
    try {
      const response: any = await this.client.get('/vendors?list=true', { skipSelectedVendor: true } as any);
      console.log('📋 listMyVendors response:', response);
      console.log('📋 response.data:', response?.data);
      
      // Backend returns the array directly in the body (successResponse wraps it)
      // Axios parses JSON, so response.data is the parsed body
      let vendors: Vendor[] = [];
      
      if (Array.isArray(response?.data)) {
        vendors = response.data as Vendor[];
      } else if (response?.data && typeof response.data === 'object') {
        // Check if it's wrapped in { data: [...] }
        if (Array.isArray(response.data.data)) {
          vendors = response.data.data as Vendor[];
        } else if (Array.isArray(response.data)) {
          vendors = response.data as Vendor[];
        } else {
          // Single vendor object
          vendors = [response.data as Vendor];
        }
      }
      
      console.log('📋 Parsed vendors:', vendors.length, vendors.map(v => ({ id: v.vendorId, name: v.businessName })));
      return vendors;
    } catch (error) {
      console.error('❌ Error in listMyVendors:', error);
      return [];
    }
  }

  async getVendor(): Promise<Vendor> {
    // GET /vendors returns current vendor's info when called by vendor
    // For vendors, it returns a single vendor object
    // For admins, it returns an array
    const response = await this.client.get<Vendor | Vendor[]>('/vendors', { skipSelectedVendor: false } as any);
    const data = response.data;
    // If it's an array (admin viewing all vendors), get first item
    if (Array.isArray(data)) {
      return data[0];
    }
    return data as Vendor;
  }

  async createVendor(vendor: Partial<Vendor>): Promise<Vendor> {
    const response = await this.client.post<Vendor>('/vendors', vendor);
    return response.data as Vendor;
  }

  async updateVendor(updates: Partial<Vendor>): Promise<Vendor> {
    // First get vendor ID
    const vendor = await this.getVendor();
    const response = await this.client.put<Vendor>(`/vendors/${vendor.vendorId}`, updates);
    return response.data as Vendor;
  }

  // Phone number management
  async activatePhoneNumber(): Promise<{ phoneNumber: string; phoneNumberId: string; message: string }> {
    const vendor = await this.getVendor();
    const response = await this.client.post(`/vendors/${vendor.vendorId}/phone/activate`, {});
    return response.data;
  }

  async getSMSSpending(): Promise<{
    phoneNumber: string | null;
    messagesSent: number;
    messagesReceived: number;
    spending: { sending: number; receiving: number; total: number };
    spendingLimit: number | null;
    period: string;
    periodStart: string;
    periodEnd: string;
  }> {
    const vendor = await this.getVendor();
    const response = await this.client.get(`/vendors/${vendor.vendorId}/phone/spending`);
    return response.data;
  }

  async setSpendingLimit(limit: number): Promise<{ spendingLimit: number; message: string }> {
    const vendor = await this.getVendor();
    const response = await this.client.put(`/vendors/${vendor.vendorId}/phone/spending-limit`, { spendingLimit: limit });
    return response.data;
  }

  async getPhoneStatus(): Promise<{
    phoneNumber: string | null;
    phoneNumberId: string | null;
    status: 'NOT_PROVISIONED' | 'PENDING' | 'ACTIVE' | 'UNKNOWN';
    numberType?: string;
    twoWayEnabled?: boolean;
    message: string;
  }> {
    const vendor = await this.getVendor();
    const response = await this.client.get(`/vendors/${vendor.vendorId}/phone/status`);
    return response.data;
  }

  async inviteAdmin(data: { email: string; firstName: string; lastName?: string }): Promise<any> {
    const response = await this.client.post('/admin/invite', data);
    return response.data;
  }

  // Analytics
  async getAnalytics(): Promise<{
    operationalMetrics: {
      totalCustomers: number;
      activeCustomers: number;
      messagesByChannel: { sms: number; email: number; whatsapp: number };
      avgResponseTime: number;
      bookingRate: number;
      escalationRate: number;
    };
    businessIntelligence: {
      customerAcquisitionRate: number;
      revenueByService: Record<string, number>;
      peakBookingHours: string[];
      customerLifetimeValue: number;
      churnRate: number;
    };
    aiAgentInsights: {
      commonIntents: Array<{ intent: string; count: number }>;
      successRate: number;
      avgConversationLength: number;
      failedIntentCount: number;
    };
  }> {
    const response = await this.client.get<ApiResponse<any>>('/analytics');
    return response.data.data!;
  }

  async getPaymentDetails(paymentId: string, vendorId: string, token?: string): Promise<any> {
    const response = await this.client.get<ApiResponse<any>>('/payments/payment-details', {
      params: { paymentId, vendorId, ...(token && { token }) },
      skipSelectedVendor: true, // Public endpoint, no vendor auth needed
    } as any);
    // Safely extract data from response
    if (response.data && response.data.data) {
      return response.data.data;
    }
    // Fallback: if response structure is different, return the whole response.data
    const responseData = response.data as any;
    if (responseData && !responseData.data && responseData.paymentId) {
      return responseData;
    }
    console.error('[API] Unexpected payment details response structure:', response.data);
    return null;
  }

  // Rewards API
  async getRewards(): Promise<Reward[]> {
    const response = await this.client.get<ApiResponse<{ rewards: Reward[] }>>('/rewards');
    return response.data.data?.rewards || [];
  }

  async getReward(rewardId: string): Promise<Reward> {
    const response = await this.client.get<ApiResponse<Reward>>(`/rewards/${rewardId}`);
    return response.data.data!;
  }

  async createReward(reward: Partial<Reward>): Promise<Reward> {
    const response = await this.client.post<ApiResponse<Reward>>('/rewards', reward);
    return response.data.data!;
  }

  async updateReward(rewardId: string, updates: Partial<Reward>): Promise<Reward> {
    const response = await this.client.put<ApiResponse<Reward>>(`/rewards/${rewardId}`, updates);
    return response.data.data!;
  }

  async deleteReward(rewardId: string): Promise<void> {
    await this.client.delete(`/rewards/${rewardId}`);
  }

  async getCustomerPoints(vendorId: string, customerId: string): Promise<CustomerPoints> {
    const response = await this.client.get<ApiResponse<CustomerPoints>>(`/rewards/points/${vendorId}/${customerId}`);
    return response.data.data!;
  }

  async getCustomerPointsHistory(vendorId: string, customerId: string): Promise<Array<{
    type: 'earned' | 'redeemed' | 'expired' | 'bonus';
    amount: number;
    description: string;
    orderId?: string;
    appointmentId?: string;
    rewardId?: string;
    timestamp: string;
  }>> {
    const response = await this.client.get<ApiResponse<{ history: any[] }>>(`/rewards/points/${vendorId}/${customerId}/history`);
    return response.data.data?.history || [];
  }

  // Customer rewards endpoints
  async getEligibleRewards(customerId: string, orderTotal?: number): Promise<Reward[]> {
    await this.getVendor();
    const params: any = {};
    if (orderTotal !== undefined) {
      params.orderTotal = Math.round(orderTotal * 100); // Convert to cents
    }
    const response = await this.client.get<ApiResponse<{ rewards: Reward[] }>>(
      `/rewards/customer/${customerId}/eligible`,
      { params }
    );
    return response.data.data?.rewards || [];
  }

  async getCustomerRewards(customerId: string, status?: 'claimed' | 'redeemed' | 'expired'): Promise<any[]> {
    await this.getVendor();
    const params: any = {};
    if (status) {
      params.status = status;
    }
    const response = await this.client.get<ApiResponse<{ rewards: any[] }>>(
      `/rewards/customer/${customerId}/claimed`,
      { params }
    );
    return response.data.data?.rewards || [];
  }

  async claimReward(customerId: string, rewardId: string): Promise<any> {
    await this.getVendor();
    const response = await this.client.post<ApiResponse<any>>(
      `/rewards/customer/${customerId}/claim/${rewardId}`
    );
    return response.data.data!;
  }

  async redeemReward(customerId: string, rewardId: string, orderId?: string, appointmentId?: string): Promise<any> {
    await this.getVendor();
    const response = await this.client.post<ApiResponse<any>>(
      `/rewards/customer/${customerId}/redeem/${rewardId}`,
      { orderId, appointmentId }
    );
    return response.data.data!;
  }
}

export const apiService = new ApiService();
export const apiClient = apiService; // Alias for direct HTTP method access

